import { z } from 'zod';
import { parseToE164 } from '../phone.js';

/**
 * @aiPurpose closed catalog of contact-identifier channels for the CRM v4
 *   global identity registry (`ContactIdentity`, #2241). One identifier of a
 *   given type is owned by exactly one Engagement / ClientContact
 *   (`@@unique([type, valueNormalized])`, INV-2).
 * @aiInvariant append-only; values are stored in registry rows forever.
 *   Mirrored by the Prisma `ContactIdentityType` enum in
 *   `apps/backend/prisma/schema/contact-identity.prisma` and verified by
 *   `scripts/check-enum-sync.sh`.
 */
export enum ContactIdentityType {
  PHONE = 'PHONE',
  TELEGRAM = 'TELEGRAM',
  WHATSAPP = 'WHATSAPP',
  EMAIL = 'EMAIL',
  MAX = 'MAX',
  OTHER = 'OTHER',
}
export const ContactIdentityTypeSchema = z.nativeEnum(ContactIdentityType);

/**
 * The canonical, comparison-ready form of one contact identifier. Two raw
 * identifiers that denote the same real contact MUST produce byte-equal
 * `valueNormalized` so the global `UNIQUE(type, valueNormalized)` constraint
 * (INV-2) dedups them.
 */
export interface NormalizedIdentity {
  readonly valueNormalized: string;
}

/**
 * @aiPurpose single source of truth for identifier normalization (#2241 FR-3),
 *   used by BOTH the write path (registry claim) and the search/dedup path so
 *   the two never diverge. Deterministic and idempotent per type.
 *
 *   PHONE / WHATSAPP parse free-form input to canonical E.164 (RU default
 *   country) so `+7 900 123-45-67` and `8 900 1234567` normalize EQUAL — the
 *   digits-only legacy `normalizeContactPhone` must NOT be used here, it does
 *   not convert bare `8…` → `+7…` and would leave the two as distinct
 *   identities, breaking INV-2 dedup. TELEGRAM strips a leading `@` and
 *   lowercases (`@User` ≡ `user`). EMAIL / MAX / OTHER trim + lowercase.
 * @aiInvariant total and deterministic: unparseable PHONE/WHATSAPP input falls
 *   back to the trimmed raw value rather than throwing, so a search path never
 *   crashes on loose input; a value that fails to reach E.164 simply will not
 *   dedup against its E.164 form (accepted — the write path validates E.164
 *   upstream via `phoneSchema`).
 * @aiPii the input and the returned `valueNormalized` can be a phone / email /
 *   messenger handle — redact in logs. Only the caught-P2002 409 surfaces the
 *   raw value, back to the caller, never to logs.
 */
export function normalizeIdentity(type: ContactIdentityType, value: string): NormalizedIdentity {
  switch (type) {
    case ContactIdentityType.PHONE:
    case ContactIdentityType.WHATSAPP: {
      const e164 = parseToE164(value);
      return { valueNormalized: e164 ?? value.trim() };
    }
    case ContactIdentityType.TELEGRAM:
      return { valueNormalized: value.trim().replace(/^@+/, '').toLowerCase() };
    case ContactIdentityType.EMAIL:
    case ContactIdentityType.MAX:
    case ContactIdentityType.OTHER:
      return { valueNormalized: value.trim().toLowerCase() };
  }
}

/**
 * @aiPurpose the discriminator + raw value pair a conflict (409) surfaces back
 *   to the caller when an identifier is already claimed (#2241 FR-4,
 *   `meta.identity`). Carries the RAW value the caller submitted (not the
 *   normalized form) so the UI can echo what the user typed.
 * @aiPii `value` is a raw identifier (phone / email / handle) — the 409 body
 *   may carry it back to the caller, but it MUST NOT reach logs.
 */
export const ContactIdentityRefSchema = z
  .object({
    type: ContactIdentityTypeSchema,
    value: z.string(),
  })
  .strict();
export type ContactIdentityRef = z.infer<typeof ContactIdentityRefSchema>;

/**
 * The stable `meta.kind` discriminator carried on the identity-conflict 409
 * (#2241 FR-4). Lives in `meta.kind`, NOT as a top-level `ErrorCode` — the
 * wire `code` stays the closed `DOMAIN_CONFLICT`.
 */
export const IDENTIFIER_TAKEN_KIND = 'IDENTIFIER_TAKEN' as const;

/**
 * @aiPurpose the owner pointer carried on the identity-conflict 409's
 *   `meta.owner` (#2242 FR-3 / AC-2) when the taken identifier resolves to a
 *   `Client`. Names the owning client id + its manager so the UI can route the
 *   operator to the current owner without a second lookup.
 * @aiPii NEVER carries contact PII (no name / phone / email) — only the client
 *   id and its manager's user id, both non-PII references.
 */
export const IdentifierTakenOwnerSchema = z
  .object({
    clientId: z.string().uuid(),
    managerId: z.string().uuid(),
  })
  .strict();
export type IdentifierTakenOwner = z.infer<typeof IdentifierTakenOwnerSchema>;

/**
 * The stable `meta.kind` discriminator carried on the active-engagement dedup
 * 409 (#2244 FR-1). A dedup search that resolves an identifier to an
 * `Engagement`-owned registry slot surfaces this instead of an auto-merge: the
 * manager must resolve the collision explicitly. Lives in `meta.kind`, NOT as a
 * top-level `ErrorCode` — the wire `code` stays the closed `DOMAIN_CONFLICT`,
 * exactly like {@link IDENTIFIER_TAKEN_KIND}.
 */
export const ENGAGEMENT_EXISTS_KIND = 'ENGAGEMENT_EXISTS' as const;

/**
 * @aiPurpose the `meta` shape of the active-engagement dedup 409 (#2244 FR-1).
 *   Names the existing `Engagement` so the UI can route the operator to it
 *   without a second lookup — no auto-merge, no forced terminal stage.
 * @aiPii carries ONLY the `engagementId` (a non-PII reference) — never the
 *   colliding identifier, contact name, or any draft PII.
 */
export const EngagementExistsMetaSchema = z
  .object({
    kind: z.literal(ENGAGEMENT_EXISTS_KIND),
    engagementId: z.string().uuid(),
  })
  .strict();
export type EngagementExistsMeta = z.infer<typeof EngagementExistsMetaSchema>;
